/**
 * Panneau de cuisine: les recettes qui demandent d'etre a l'interieur.
 * Le clic lance une petite cuisson de deux secondes, puis annonce la recette
 * au jeu par `onCuisiner`.
 */
import type { Inventaire, Recette } from '../../shared/types/items.ts'
import { objet } from '../../shared/data/objets.ts'
import { RECETTES_CUISINE } from '../../shared/data/recettes.ts'
import { peutPayer } from '../../shared/sim/inventaire.ts'
import { accorde } from '../../shared/sim/grammaire.ts'
import { journal } from '../log/journal.ts'
import { Panneau, afficherCout, clicSonore, el, grille, refusSonore } from './commun.ts'

/** Duree de l'animation de cuisson, en ms. */
const DUREE_CUISSON = 2000

export class PanneauCuisine extends Panneau {
  /** Le joueur lance une recette. Appele a la fin de la cuisson. */
  onCuisiner: ((idRecette: string) => void) | null = null

  private inv: Inventaire = {}
  private cuisson: number | null = null
  private banniere: HTMLDivElement

  constructor(parent: HTMLElement) {
    super(parent, 'cuisine', 'Cuisine', '🍳', 'panneau-fond--cuisine')
    this.banniere = el('div', 'banniere banniere--succes')
    this.banniere.hidden = true
  }

  ouvrir(inv: Inventaire): void {
    this.inv = inv
    this.construire()
    this.afficher(`${RECETTES_CUISINE.length} recettes`)
  }

  /** Reconstruit la liste (par exemple apres avoir cuisine). */
  rafraichir(inv: Inventaire): void {
    this.inv = inv
    if (this.ouvert && this.cuisson === null) this.construire()
  }

  private construire(): void {
    this.viderCorps()
    this.viderPied()
    this.corps.append(this.banniere)

    const g = grille('grille--recettes')
    for (const r of RECETTES_CUISINE) g.append(this.carte(r))
    this.corps.append(g)
    if (RECETTES_CUISINE.length === 0) {
      this.corps.append(el('p', 'vide', 'Aucune recette pour l’instant.'))
    }
    this.pied.append(
      el('span', 'pied__note', 'Les plats se préparent seulement à l’intérieur, sur le comptoir de la cuisine.'),
    )
  }

  private carte(r: Recette): HTMLElement {
    const def = objet(r.produit)
    const possible = peutPayer(this.inv, r.cout)
    const carte = el('div', 'carte carte--recette')
    if (!possible) carte.classList.add('carte--bloque')

    const haut = el('div', 'carte__haut')
    haut.append(el('span', 'carte__icone', r.icone))
    haut.append(el('span', 'carte__nom', r.nom))
    carte.append(haut)

    carte.append(el('div', 'carte__etiquette', 'Il faut'))
    carte.append(afficherCout(r.cout, this.inv))

    const rend = el('div', 'effets')
    rend.append(el('span', 'effet effet--produit', `${def.icone} × ${r.quantite}`))
    if (def.faim > 0) rend.append(el('span', 'effet effet--faim', `+${def.faim} faim`))
    if (def.energie > 0) rend.append(el('span', 'effet effet--energie', `+${def.energie} énergie`))
    carte.append(el('div', 'carte__etiquette', 'Ça donne'))
    carte.append(rend)

    const b = el('button', 'bouton bouton--vert carte__action')
    b.type = 'button'
    b.textContent = '🍳 Cuisiner'
    b.addEventListener('click', () => {
      if (!possible) {
        refusSonore()
        journal.input('cuisine', `recette ${r.id} refusee: ingredients manquants`)
        carte.classList.remove('carte--refus')
        void carte.offsetWidth
        carte.classList.add('carte--refus')
        return
      }
      clicSonore()
      this.cuire(r, carte)
    })
    carte.append(b)
    return carte
  }

  /** Deux secondes de cuisson, puis on annonce le resultat. */
  private cuire(r: Recette, carte: HTMLElement): void {
    if (this.cuisson !== null) return
    journal.input('cuisine', `cuisson de ${r.id} (${r.nom})`, { cout: r.cout })
    this.banniere.hidden = true
    this.bloquerBoutons(true)
    const voile = el('div', 'cuisson')
    voile.append(el('span', 'cuisson__icone', '🍳'))
    const piste = el('div', 'cuisson__piste')
    const barre = el('div', 'cuisson__barre')
    piste.append(barre)
    voile.append(piste)
    voile.append(el('span', 'cuisson__texte', 'Ça cuit…'))
    carte.append(voile)
    requestAnimationFrame(() => {
      barre.style.transitionDuration = `${DUREE_CUISSON}ms`
      barre.style.width = '100%'
    })

    this.cuisson = window.setTimeout(() => {
      this.cuisson = null
      voile.remove()
      this.bloquerBoutons(false)
      this.banniere.textContent = `✅ ${r.nom} est ${accorde('prêt', r)} !`
      this.banniere.hidden = false
      journal.input('cuisine', `cuisson terminee: ${r.id}`)
      this.onCuisiner?.(r.id)
    }, DUREE_CUISSON)
  }

  private bloquerBoutons(bloque: boolean): void {
    for (const b of this.corps.querySelectorAll<HTMLButtonElement>('button.carte__action')) {
      b.disabled = bloque
    }
  }

  protected override surFermeture(): void {
    if (this.cuisson !== null) {
      window.clearTimeout(this.cuisson)
      this.cuisson = null
      journal.input('cuisine', 'cuisson interrompue par la fermeture du panneau')
    }
    this.banniere.hidden = true
  }

  override detruire(): void {
    if (this.cuisson !== null) window.clearTimeout(this.cuisson)
    this.cuisson = null
    this.onCuisiner = null
    super.detruire()
  }
}
